import { useRef, useState, useEffect } from "react";

interface UseStreamingHtmlAnimationOptions {
  onEnd?: () => void;
  delay?: number;
}

export default function useStreamingHTMLAnimation(rawHTML:string, {
  onEnd,
  delay = 20
}: UseStreamingHtmlAnimationOptions) {
  const [prevRawHTML, setPrevRawHTML] = useState<string>(rawHTML);
  const [displayedHTML, setDisplayedHTML] = useState<string>(rawHTML || '');
  const tokensRef = useRef<string[]>([]);
  const indexRef = useRef<number>(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (rawHTML !== prevRawHTML) {
      setDisplayedHTML(rawHTML);
      setPrevRawHTML(rawHTML);
    }
  }, [rawHTML, prevRawHTML]);

  const onStart = () => {
    tokensRef.current = tokenizeHtml(rawHTML);
    setDisplayedHTML('');
    indexRef.current = 0;

    function step() {
      // Only append if index is in bounds
      if (indexRef.current < tokensRef.current.length) {
        const token = tokensRef.current[indexRef.current];
        if (token !== undefined) {
          setDisplayedHTML((prev) => prev + token);
        }
        indexRef.current += 1;
        if (indexRef.current < tokensRef.current.length) {
          timerRef.current = setTimeout(step, delay);
        } else {
          if (onEnd) onEnd();
        }
      }
    }

    if (tokensRef.current.length > 0) {
      timerRef.current = setTimeout(step, delay);
    } else {
      if (onEnd) onEnd();
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return { displayedHTML, onStart };
};


/**
 * Splits HTML into tag-safe tokens (full tags or text between tags)
 */
function tokenizeHtml(html:string) {
  const regex = /(<[^>]+>|[^<]+)/g;
  return html.match(regex) || [];
}